"use client"

import { Component, useEffect, useRef } from "react"
import type { MouseEvent, ReactNode } from "react"

import { collectFeedProducts, paintedFeedProducts } from "@/features/feed/collect-feed-products"
import type { FeedPage, FeedSection } from "@/features/feed/contract"
import { isStandaloneSectionType } from "@/features/feed/contract/section-types"
import { buildGA4ItemFromFeedProduct } from "@/features/feed/feed-product-analytics"
import { useSectionReserve } from "@/features/feed/section-heights"
import { useScreenVisible } from "@/platform/app-mode/use-screen-visible"
import { trackFeedSectionClick, trackFeedSectionImpression } from "@/platform/observability/analytics/v2/feed"
import { trackSelectItem, trackViewItemList } from "@/platform/observability/analytics/v2/search"
import type { FeedSectionEventParams } from "@/platform/observability/analytics/v2/types/custom"
import { ImpressionsTrackingScope } from "@/platform/observability/impressions/tracking-scope"
import { useImpression, useImpressionsWithin } from "@/platform/observability/impressions/use-impression"
import { recordServeClick } from "@/platform/observability/impressions/visibility"
import { logger } from "@/platform/observability/logger"

interface SectionErrorBoundaryProps {
  sectionId: string
  sectionType: string
  children: ReactNode
}

// A broken section costs that one section, never the page.
class SectionErrorBoundary extends Component<SectionErrorBoundaryProps, { hasError: boolean }> {
  override state = { hasError: false }

  static getDerivedStateFromError() {
    return { hasError: true }
  }

  override componentDidCatch(error: Error) {
    logger.error("feed.section.render_failed", { sectionId: this.props.sectionId, sectionType: this.props.sectionType }, error)
  }

  override render() {
    return this.state.hasError ? null : this.props.children
  }
}

const listIdentifiers = (section: FeedSection) => ({ item_list_id: section.analyticsKey, item_list_name: section.analyticsKey })

const sectionEventParams = (section: FeedSection, page: FeedPage, position: number): FeedSectionEventParams => ({
  section_id: section.id,
  section_type: section.type,
  section_variant: section.presentation.variant,
  section_position: position,
  analytics_key: section.analyticsKey,
  page_handle: page.handle,
  page_type: page.pageType,
})

interface SectionShellProps {
  section: FeedSection
  page: FeedPage
  position: number
  fullBleed: boolean
  // Per-form-factor top margin computed by FeedSections — the shell owns no rhythm.
  flowClass: string
  // Row members are tracks inside the row's grid: the row owns cap/gutter/rhythm
  // (gut-flush zeroes the member's whole gut family), and the shell becomes a
  // container so content adapts to its track width. Below the row's grid step the
  // row is a reel instead, and a reel item needs a width of its own — a grid track
  // gets one from the template, a flex child would size to its content.
  //
  // A standalone section is a container for the same reason. It used to be the one
  // branch that wasn't, and the whole gut family is a CONTAINER query — so `px-gut`
  // inside a standalone section never found a container and silently sat on its 16px
  // mobile fallback at every width. Section headers land in exactly that spot, so on
  // desktop a header sat 16px in while its own rail sat 32px in, and which of the two
  // you got depended on whether the component happened to own its header inside a
  // container of its own. Measured 2026-08-26 before the fix: 7 of 10 home headers on
  // the mobile gutter at 1440, and every header on categories and collections.
  inRow?: boolean
  track: boolean
  children: ReactNode
}

const FORM_FACTOR_CLASS = {
  mobileOnly: "@3xl:hidden",
  desktopOnly: "hidden @3xl:block",
  all: "",
}

const formFactorClass = (section: FeedSection) => {
  const { formFactors } = section.presentation
  if (formFactors.includes("MOBILE") && !formFactors.includes("DESKTOP")) return FORM_FACTOR_CLASS.mobileOnly
  if (formFactors.includes("DESKTOP") && !formFactors.includes("MOBILE")) return FORM_FACTOR_CLASS.desktopOnly
  return FORM_FACTOR_CLASS.all
}

export const SectionShell = ({ section, page, position, fullBleed, flowClass, inRow = false, track, children }: SectionShellProps) => {
  const visible = useScreenVisible()
  const ref = useRef<HTMLDivElement>(null)
  const impressionFired = useRef(false)
  useEffect(() => {
    const node = ref.current
    if (!visible || !track || !node || impressionFired.current) return
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry?.isIntersecting || impressionFired.current) return
        impressionFired.current = true
        trackFeedSectionImpression(sectionEventParams(section, page, position))
        const products = paintedFeedProducts(section, node)
        if (products.length > 0) {
          const list = listIdentifiers(section)
          trackViewItemList({ ...list, items: products.map((product, index) => buildGA4ItemFromFeedProduct(product, { ...list, index })) })
        }
        observer.disconnect()
      },
      { threshold: 0.2 }
    )
    observer.observe(node)
    return () => observer.disconnect()
  }, [track, section, page, position, visible])
  const deferred = position >= 2
  const reserve = useSectionReserve(ref, section.id, deferred)
  useImpression(ref, track ? section.serveId : undefined, { container: true })
  useImpressionsWithin(ref, track)

  const handleClickCapture = (event: MouseEvent<HTMLDivElement>) => {
    if (!track) return
    recordServeClick(event.nativeEvent)
    const link = (event.target as HTMLElement).closest("a")
    if (!link) return
    trackFeedSectionClick({ ...sectionEventParams(section, page, position), link_url: link.getAttribute("href") ?? "" })

    const itemId = link.closest("[data-nelo-item]")?.getAttribute("data-nelo-item")
    if (!itemId) return
    const products = collectFeedProducts(section)
    const index = products.findIndex((product) => product.id === itemId)
    const product = products[index]
    if (!product) return
    const list = listIdentifiers(section)
    trackSelectItem({ ...list, items: [buildGA4ItemFromFeedProduct(product, { ...list, index })] })
  }

  return (
    <div
      ref={ref}
      data-feed-section={section.id}
      data-feed-section-type={section.type}
      data-feed-section-variant={section.presentation.variant}
      data-nelo-serve={section.serveId}
      className={`${deferred ? "cv-section" : ""} ${formFactorClass(section)} ${inRow ? `${isStandaloneSectionType(section.type) ? "" : "gut-flush"} @container w-4/5 min-w-0 snap-start @5xl:w-auto` : fullBleed ? "" : "max-w-page @container mx-auto w-full"} ${flowClass}`}
      style={reserve}
      onClickCapture={handleClickCapture}
    >
      <SectionErrorBoundary sectionId={section.id} sectionType={section.type}>
        <ImpressionsTrackingScope value={track}>{children}</ImpressionsTrackingScope>
      </SectionErrorBoundary>
    </div>
  )
}
